/**
 * @fileoverview Custom hook for debouncing primitive string values.
 */

import {useEffect, useMemo, useState} from "react";
import {debounce} from "lodash";

/**
 * Custom hook for debouncing textual input fields.
 * The debounced value will only update after the specified timeout period
 * has passed without the input value changing.
 *
 * @param {string} value The string value to debounce.
 * @param {number} [timeout=1000] The debounce timeout in milliseconds.
 * @returns {string} The debounced string value.
 */
export const useDebouncedPrimitiveValue = (value: string, timeout = 1000) => {
  const [debounced, setDebounced] = useState(value);

  const debounceValue = useMemo(
    () => debounce(value => setDebounced(value), timeout),
    [setDebounced, timeout],
  );

  useEffect(() => {
    debounceValue(value);
  }, [value, debounceValue]);

  return debounced;
};
